import { EtiquetaDePagina, CLASE_TITULO_DE_PAGINA } from '@/components/patterns';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent } from '@/components/ui/card';
import { PersonasEliminadas } from '../components/personas-eliminadas';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Users, Ban, Search, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { traerPadron, type PersonaDelPadron } from '../services/personas.service';

/** Los cuatro roles del sistema, en castellano llano. Nunca el nombre del enum en pantalla. */
const ROLE_LABELS: Record<string, string> = {
  PLATFORM_ADMIN: 'Superadmin',
  COMPANY_ADMIN: 'Admin de empresa',
  SALESPERSON: 'Vendedor',
  APPROVER: 'Aprobador',
};

function etiquetaDeRol(rol: string): string {
  return ROLE_LABELS[rol] || rol;
}

/**
 * Cuándo entró por última vez, o que nunca entró, o que la fecha no se pudo
 * leer.
 *
 * Tres estados y tres mensajes distintos, a propósito: `null` no es lo mismo
 * que un string no parseable, y ninguno de los dos es una celda vacía. Una
 * celda vacía se lee igual que "no sé", y acá siempre se sabe algo.
 */
function UltimoIngreso({ ultimoIngreso }: { ultimoIngreso: string | null }) {
  if (!ultimoIngreso) {
    return <span className="text-muted-foreground">Nunca entró</span>;
  }
  const fecha = new Date(ultimoIngreso);
  if (Number.isNaN(fecha.getTime())) {
    // El formato lo controla el backend, pero "Invalid Date" en pantalla es
    // peor que admitir que no se pudo leer.
    return <span className="text-muted-foreground">Fecha no disponible</span>;
  }
  return <span>{fecha.toLocaleString('es-BO')}</span>;
}

/**
 * Hasta cuándo puede entrar, y si ya se le pasó.
 *
 * 🔴 Esta columna existía en el listado que el padrón reemplazó y se había
 * perdido. Sin ella, fijar una fecha de expiración es una acción que nadie va a
 * usar: para saber a quién le venció habría que abrir las fichas de a una.
 *
 * «Sin límite» y no una celda vacía, por lo mismo que `UltimoIngreso`: una celda
 * vacía se lee igual que «no sé», y acá siempre se sabe.
 */
function Expiracion({ expiraEl }: { expiraEl: string | null }) {
  if (!expiraEl) {
    return <span className="text-muted-foreground">Sin límite</span>;
  }
  const fecha = new Date(expiraEl);
  if (Number.isNaN(fecha.getTime())) {
    return <span className="text-muted-foreground">Fecha no disponible</span>;
  }
  const vencido = fecha < new Date();
  return (
    <span className={vencido ? 'text-destructive font-medium' : undefined}>
      {fecha.toLocaleDateString('es-BO')}
      {vencido && ' (vencido)'}
    </span>
  );
}

/**
 * Los portales a los que entra, una pastilla por portal con el nombre de la
 * empresa (nunca el slug: es un identificador interno).
 */
function PortalesDeLaPersona({
  portales,
}: {
  portales: { slug: string; empresa: string; rol: string }[];
}) {
  if (portales.length === 0) {
    return <span className="text-muted-foreground">Sin portales</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {portales.map((p, i) => (
        <Badge key={`${p.slug}-${i}`} variant="outline">
          {p.empresa}
        </Badge>
      ))}
    </div>
  );
}

/** El rol que tiene en cada portal, en el mismo orden que la columna de portales. */
function RolesDeLaPersona({ portales }: { portales: { rol: string }[] }) {
  if (portales.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-col gap-1">
      {portales.map((p, i) => (
        <span key={i} className="text-sm">
          {etiquetaDeRol(p.rol)}
        </span>
      ))}
    </div>
  );
}

/**
 * Que la cuenta esté desactivada, dicho con todas las letras.
 *
 * `activo` es la CUENTA, no la membresía: el login corta antes que nada si la
 * cuenta está desactivada, así que sus portales quedan listados —son
 * membresías reales, el padrón es un censo— pero no le sirven para entrar.
 * Ocultar esto sería mentir por omisión: la fila tiene que decir "figura,
 * tiene estos portales, y aun así no puede entrar".
 */
function AvisoDeCuentaDesactivada() {
  return (
    <div className="flex items-center gap-1 text-xs text-destructive mt-1">
      <Ban className="h-3 w-3" aria-hidden="true" />
      <span>Cuenta desactivada: no puede entrar a estos portales</span>
    </div>
  );
}

const SIN_FILTRO = 'all';

export function AdminPersonasPage() {
  const navigate = useNavigate();
  const [personas, setPersonas] = useState<PersonaDelPadron[]>([]);
  const [cargando, setCargando] = useState(true);
  /*
    🔴 «Falló» no es «está vacío». Sin este estado, la cadena era
    `.then(...).finally(...)` sin `.catch`: un 500, un 403 o la red caída
    dejaban la lista vacía y `cargando` en false, y la pantalla dibujaba «No hay
    personas en el padrón» — acusando al censo de estar vacío cuando lo único
    que pasó es que el pedido no llegó (y encima quedaba una promesa rechazada
    sin manejar). Es el mismo criterio que ya aplica la pantalla de portales.
  */
  const [errorAlCargar, setErrorAlCargar] = useState(false);
  const [intento, setIntento] = useState(0);
  const [busqueda, setBusqueda] = useState('');
  const [filtroRol, setFiltroRol] = useState(SIN_FILTRO);
  const [filtroEstado, setFiltroEstado] = useState(SIN_FILTRO);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    setErrorAlCargar(false);
    traerPadron()
      .then((data) => {
        if (!cancelado) setPersonas(data);
      })
      .catch(() => {
        if (!cancelado) setErrorAlCargar(true);
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [intento]);

  /*
    Todo se filtra del lado del cliente: el endpoint del padrón no pagina,
    trae todas las filas juntas, así que ya está todo en memoria y acotar es
    instantáneo sin tocar el backend (decisión del coordinador — 54 personas
    en producción, no hace falta paginar para eso).
  */
  const personasFiltradas = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    return personas.filter((persona) => {
      const coincideBusqueda =
        !termino ||
        persona.nombre.toLowerCase().includes(termino) ||
        persona.email.toLowerCase().includes(termino);

      // Una persona puede tener roles DISTINTOS en portales distintos: se
      // queda si tiene el rol buscado en ALGUNO de sus portales, no solo en
      // el primero — filtrar por "el rol principal" mentiría.
      const coincideRol =
        filtroRol === SIN_FILTRO || persona.portales.some((p) => p.rol === filtroRol);

      const coincideEstado =
        filtroEstado === SIN_FILTRO ||
        (filtroEstado === 'activa' && persona.activo) ||
        (filtroEstado === 'desactivada' && !persona.activo);

      return coincideBusqueda && coincideRol && coincideEstado;
    });
  }, [personas, busqueda, filtroRol, filtroEstado]);

  const conVariosPortales = personas.filter((p) => p.portales.length > 1).length;

  return (
    <AppLayout variante="plataforma">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6" />
            <div className="mb-3">
              <EtiquetaDePagina tono="neutro">Plataforma</EtiquetaDePagina>
            </div>
            <h1 className={CLASE_TITULO_DE_PAGINA}>Personas</h1>
            {!cargando && (
              <span className="text-sm text-muted-foreground tabular-nums">
                {personas.length} en total
                {conVariosPortales > 0 && ` · ${conVariosPortales} con más de un portal`}
              </span>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 flex-wrap items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre o correo..."
              className="pl-9"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
          {/*
            Selects nativos y no el `Select` de Radix que usan las otras
            pantallas del panel: no hay en este repo un precedente de test
            estable que simule abrir su listbox en jsdom, y estos dos
            filtros no ganan nada visual con el desplegable custom que no se
            pierda al hacerlos triviales de operar por teclado y de testear.
          */}
          <select
            aria-label="Filtrar por rol"
            value={filtroRol}
            onChange={(e) => setFiltroRol(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value={SIN_FILTRO}>Todos los roles</option>
            {Object.entries(ROLE_LABELS).map(([codigo, etiqueta]) => (
              <option key={codigo} value={codigo}>
                {etiqueta}
              </option>
            ))}
          </select>
          <select
            aria-label="Filtrar por estado"
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value={SIN_FILTRO}>Todos los estados</option>
            <option value="activa">Activas</option>
            <option value="desactivada">Desactivadas</option>
          </select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {cargando ? (
              <div className="p-6 space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : errorAlCargar ? (
              <div className="flex flex-col items-center gap-3 p-10 text-center">
                <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden="true" />
                <div>
                  <p className="font-medium">No se pudo cargar el padrón</p>
                  <p className="text-sm text-muted-foreground">
                    Puede ser un problema de conexión. Probá de nuevo.
                  </p>
                </div>
                <Button variant="outline" onClick={() => setIntento((n) => n + 1)}>
                  Reintentar
                </Button>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Persona</TableHead>
                      <TableHead>Portales a los que entra</TableHead>
                      <TableHead>Rol en cada uno</TableHead>
                      <TableHead>Último ingreso</TableHead>
                      <TableHead>Expira</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {personasFiltradas.map((persona) => (
                      <TableRow
                        key={persona.id}
                        role="link"
                        tabIndex={0}
                        aria-label={`Abrir ${persona.nombre}`}
                        className="cursor-pointer hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                        onClick={() => navigate(`/plataforma/personas/${persona.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/plataforma/personas/${persona.id}`);
                          }
                        }}
                      >
                        <TableCell>
                          <div className="font-medium">{persona.nombre}</div>
                          <div className="text-xs text-muted-foreground">{persona.email}</div>
                          {!persona.activo && <AvisoDeCuentaDesactivada />}
                        </TableCell>
                        <TableCell>
                          <PortalesDeLaPersona portales={persona.portales} />
                        </TableCell>
                        <TableCell>
                          <RolesDeLaPersona portales={persona.portales} />
                        </TableCell>
                        <TableCell>
                          <UltimoIngreso ultimoIngreso={persona.ultimoIngreso} />
                        </TableCell>
                        <TableCell>
                          <Expiracion expiraEl={persona.expiraEl} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {personasFiltradas.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          {personas.length === 0
                            ? 'No hay personas en el padrón'
                            : 'Ninguna persona coincide con el filtro'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* L5 (1.3): las eliminadas no figuran arriba; se restauran desde acá. */}
        <PersonasEliminadas />
      </div>
    </AppLayout>
  );
}
